import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  ArrowRight,
  Star,
  Users,
  Clock,
  Tag,
  DollarSign,
  Sparkles,
  Play,
  Percent,
  MapPin,
  Zap,
  Award,
  Shield,
  Headphones,
} from "lucide-react";
import { Car, Map, TreePalm, Home } from "lucide-react";

import Banner from "../Images/hero.jpg";

const MotionButton = motion.button;
const TABS = [
  {
    id: "cabs",
    label: "Cabs",
    icon: Car,
    to: "/bookcabs",
    base: "text-gray-600",
    hover: "hover:text-yellow-500",
    active: "text-yellow-500",
  },
  {
    id: "tours",
    label: "Tours",
    icon: Map,
    to: "/packages",
    base: "text-gray-600",
    hover: "hover:text-blue-400",
    active: "text-blue-400",
  },
  {
    id: "retreats",
    label: "Retreats",
    icon: TreePalm,
    to: "/wellness",
    base: "text-gray-600",
    hover: "hover:text-green-400",
    active: "text-green-400",
  },
  {
    id: "stays",
    label: "Stays",
    icon: Home,
    to: "/hotels",
    base: "text-gray-600",
    hover: "hover:text-purple-400",
    active: "text-purple-400",
  },
];



function FloatingStats({ count }) {
  return (
    <>
      {/* Floating Price Badge - Right */}
      <motion.div
        className="absolute top-48 right-8 md:right-20 z-20"
        initial={{ opacity: 0, x: 100, rotate: -15 }}
        animate={{ opacity: 1, x: 0, rotate: -5 }}
        transition={{ delay: 1, duration: 1, type: "spring" }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl p-5 md:p-7 backdrop-blur-sm"
          animate={{
            y: [0, -15, 0],
            rotate: [-5, -2, -5],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          whileHover={{ scale: 1.1, rotate: 0 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Tag className="w-6 h-6 text-teal-600" />
            </motion.div>
            <span className="text-xs text-gray-600">Limited Time Offer</span>
          </div>
          <div className="text-3xl text-teal-600">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              Save {count.tours}%
            </motion.span>
          </div>
          <div className="text-xs text-gray-500">on relaxation tours</div>
        </motion.div>
      </motion.div>

      {/* Floating Price Badge - Left */}
      <motion.div
        className="absolute bottom-32 left-8 md:left-20 z-20"
        initial={{ opacity: 0, x: -100, rotate: 15 }}
        animate={{ opacity: 1, x: 0, rotate: 5 }}
        transition={{ delay: 1.3, duration: 1, type: "spring" }}
      >
        <motion.div
          className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-2xl p-5 md:p-7 text-white relative overflow-hidden"
          animate={{
            y: [0, -12, 0],
            rotate: [5, 2, 5],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          whileHover={{ scale: 1.1, rotate: 0 }}
        >
          {/* Shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{
              x: ["-100%", "200%"],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatDelay: 2,
            }}
          />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-6 h-6" />
              <span className="text-xs">Starting from</span>
            </div>
            <div className="text-4xl">₹{count.rides.toFixed(2)}</div>
            <div className="text-xs opacity-90">per ride • Economy</div>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}

const Hero = () => {
  const image = Banner;
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const [activeTab, setActiveTab] = useState(null);
  const [count, setCount] = useState({
    rides: 0,
    tours: 0,
    destinations: 0,
    travelers: 0,
  });

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCount({
        rides: Math.floor(9.99 * progress * 100) / 100,
        tours: Math.floor(40 * progress),
        destinations: Math.floor(500 * progress),
        travelers: Math.floor(50000 * progress),
      });

      if (currentStep >= steps) {
        setCount({ rides: 600, tours: 40, destinations: 500, travelers: 50000 });
        clearInterval(timer);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full h-[100vh] overflow-hidden font-lato">
      {/* Static Background Image */}
      <div
        className="absolute inset-0 w-full h-full bg-center bg-cover"
        style={{
          backgroundImage: `url(${image})`,
          filter: "brightness(0.9)",
        }}
      />
      {/* Overlay Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 mt-12 md:mt-20">
      <h1 className="text-3xl md:text-6xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
  Welcome to The Great Himalayas!
</h1>

{/* Subtitle */}
<motion.p
  className="text-xl md:text-2xl lg:text-3xl text-white mb-4 max-w-3xl mx-auto mt-2"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 1.2, duration: 0.8 }}
>
 Discover Soulful Retreats, Scenic Tours, Cozy Stays & Effortless Journeys
</motion.p>
<motion.p
  className="text-base md:text-lg lg:text-xl text-white/90 mb-12 max-w-2xl mx-auto"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 1.4, duration: 0.8 }}
>
 Curated wellness escapes, Himalayan tours, handpicked hotels and homestays, and affordable cab rides—all designed to make your travel peaceful and memorable.
</motion.p>

<div className="mt-8 mb-6 flex justify-center w-full px-2">
  <div
    className="
      flex
      items-stretch
      bg-white/80
      rounded-xl
      backdrop-blur-sm
      border border-gray-200/80
      px-3 py-1
      gap-4
      sm:px-6 sm:py-2 sm:gap-10
    "
  >
  {TABS.map(({ id, label, icon: Icon, to, base, hover, active }) => {
  const isActive = activeTab === id;

  const textClasses = isActive ? active : `${base} ${hover}`;

  return (
    <Link key={id} to={to} onClick={() => setActiveTab(id)}>
      <button
        className={`
          flex flex-col items-center justify-center
          flex-1 min-w-16 sm:min-w-24 md:min-w-32 lg:min-w-40
          px-2 pt-1 pb-2 text-xs sm:text-sm
          transition-colors duration-200
          ${textClasses}
        `}
      >
        <Icon
          className={`
            mb-1 w-5 h-5 sm:w-6 sm:h-6
            transition-colors duration-200
            ${textClasses}
          `}
        />
        <span className="font-medium">{label}</span>
        {isActive && (
          <span
            className="
              mt-1 h-0.5
              w-2/3 sm:w-3/4 md:w-4/5
              bg-blue-600 rounded-full mx-auto
            "
          />
        )}
      </button>
    </Link>
  );
})}

  </div>
</div>


{/* Buttons Container 
<motion.div
  className="flex flex-wrap gap-4 justify-center mb-12"
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 1.6, duration: 0.8 }}
>
  <motion.div whileHover={{ scale: 1.08, y: -5 }} whileTap={{ scale: 0.95 }}>
    <Link to="/bookCabs">
      <Button
        size="large"
        variant="contained"
        className=" text-teal-600 bg-gray-100 hover:bg-gray-100 hover:text-blue-600 shadow-2xl text-lg px-8 py-6"
      >
        Book Your Cab
      </Button>
    </Link>
  </motion.div>
<motion.div whileHover={{ scale: 1.08, y: -5 }} whileTap={{ scale: 0.95 }}>
    <Link to="/relaxationpackage">
      <Button
        size="large"
        variant="contained"
        sx={{
    backgroundColor: 'green',
    color: 'white',
    '&:hover': {
      backgroundColor: 'darkgreen',
    }
  }}
        className=" text-white bg-green-600 hover:bg-green-700 shadow-2xl text-lg px-8 py-6"
      >
        Book Your Retreat
      </Button>
    </Link>
  </motion.div>

  <motion.div whileHover={{ scale: 1.08, y: -5 }} whileTap={{ scale: 0.95 }}>
    <Link to="/packages">
      <Button
        size="large"
        variant="contained"
        className=" text-white hover:bg-blue-700 shadow-2xl text-lg px-8 py-6"
      >
        Book Your Tour
      </Button>
    </Link>
  </motion.div>
   
</motion.div>
*/}
      </div>

   
      {/* Main Heading with animated scroll effect */}
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 max-w-7xl w-full px-4 sm:px-6 lg:px-8"
        style={{ y, opacity }}
      >
        <motion.h2
          className="text-4xl md:text-5xl text-white font-extrabold text-center drop-shadow-xl"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          
        </motion.h2>
      </motion.div>
    </div>
  );
};

export default Hero;
